import { Box, Chip, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";

interface Props {
    desde: Dayjs;
    hasta: Dayjs;
    onDesdeChange: (v: Dayjs) => void;
    onHastaChange: (v: Dayjs) => void;
}

const presets = [
    {
        label: "Este mes",
        get: () => ({ desde: dayjs().startOf("month"), hasta: dayjs().endOf("month") }),
    },
    {
        label: "Mes anterior",
        get: () => ({
            desde: dayjs().subtract(1, "month").startOf("month"),
            hasta: dayjs().subtract(1, "month").endOf("month"),
        }),
    },
    {
        label: "Últimos 7 días",
        get: () => ({ desde: dayjs().subtract(6, "day").startOf("day"), hasta: dayjs().endOf("day") }),
    },
    {
        label: "Este año",
        get: () => ({ desde: dayjs().startOf("year"), hasta: dayjs().endOf("year") }),
    },
];

export default function DateRangeFilter({ desde, hasta, onDesdeChange, onHastaChange }: Props) {
    function isActive(preset: typeof presets[number]) {
        const p = preset.get();
        return p.desde.isSame(desde, "day") && p.hasta.isSame(hasta, "day");
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1.75,
                borderRadius: "16px",
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <DatePicker
                    label="Desde"
                    value={desde}
                    onChange={(v) => v && onDesdeChange(v)}
                    slotProps={{ textField: { size: "small", sx: { width: 148 } } }}
                />
                <Typography variant="body2" color="text.disabled">—</Typography>
                <DatePicker
                    label="Hasta"
                    value={hasta}
                    onChange={(v) => v && onHastaChange(v)}
                    slotProps={{ textField: { size: "small", sx: { width: 148 } } }}
                />
            </Stack>

            <Box sx={{ width: "1px", height: 28, bgcolor: "divider", display: { xs: "none", sm: "block" } }} />

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {presets.map((p) => (
                    <Chip
                        key={p.label}
                        label={p.label}
                        size="small"
                        variant={isActive(p) ? "filled" : "outlined"}
                        color={isActive(p) ? "primary" : "default"}
                        onClick={() => {
                            const { desde: d, hasta: h } = p.get();
                            onDesdeChange(d);
                            onHastaChange(h);
                        }}
                        sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                    />
                ))}
            </Stack>
        </Box>
    );
}
